/**
 * Step4Pricing
 * Pricing information: plan builder with multiple plans,
 * free tier option, contact for pricing, CAD-locked currency,
 * and pricing transparency pledge.
 */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import PlanBuilder from '../../components/PlanBuilder';

export function validateStep4(data) {
  const errors = {};
  if (!data.plans || data.plans.length === 0) {
    errors.plans = 'Please add at least one pricing plan';
  } else {
    const hasInvalidPlan = data.plans.some((plan) => !plan.name?.trim());
    if (hasInvalidPlan) {
      errors.plans = 'All plans must have a name';
    }
  }
  return errors;
}

export default function Step4Pricing({ data, onChange, errors }) {
  const handlePlansChange = useCallback((plans) => {
    onChange({ ...data, plans });
  }, [data, onChange]);

  const handleToggle = useCallback((e) => {
    const { name, checked } = e.target;
    onChange({ ...data, [name]: checked });
  }, [data, onChange]);

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={sectionTitleStyle}>Pricing Information</h2>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Define your pricing plans. All prices are in Canadian Dollars (CAD).
        </p>
      </div>

      {/* Currency badge */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        borderRadius: '8px',
        backgroundColor: '#f0fdf4',
        border: '1px solid #86efac',
        marginBottom: '20px',
      }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#166534' }}>
          Currency: CAD ($)
        </span>
        <span style={{ fontSize: '11px', color: '#22c55e' }}>
          Canadian Dollars
        </span>
      </div>

      {/* Plan builder */}
      <div style={{ marginBottom: '20px' }}>
        <PlanBuilder
          plans={data.plans || []}
          onChange={handlePlansChange}
          error={errors.plans}
        />
      </div>

      {/* Pricing transparency pledge */}
      <div style={{
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        padding: '20px',
        border: '1px solid #e2e8f0',
      }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
          Pricing Transparency Pledge
        </h3>
        <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px', lineHeight: '1.6' }}>
          SM-Compare is committed to pricing transparency. By checking the box below, you agree
          that the pricing information provided is accurate and up-to-date. You commit to notifying
          SM-Compare of any pricing changes within 7 business days.
        </p>
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            name="pricingPledge"
            checked={data.pricingPledge || false}
            onChange={handleToggle}
            style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
          />
          <span style={{ fontSize: '13px', color: '#1e293b', lineHeight: '1.5' }}>
            I pledge that the pricing information provided is accurate and I will update it
            promptly if any changes occur.
          </span>
        </label>
      </div>
    </div>
  );
}

Step4Pricing.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
};

const sectionTitleStyle = {
  fontSize: '20px',
  fontWeight: '700',
  color: '#1e293b',
  marginBottom: '6px',
};
